import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { i18nReady } from './i18n'
import './stores/accessibility.store'
import { App } from './App'
import { createAppRouter } from './router'
import { postActivity, SponsorApiError } from './lib/api'
import { createActivityTracker, installActivityTracking, routePattern } from './lib/activity'
import { PUBLIC_PATHS, rememberReturnPath, safeReturnPath } from './lib/navigation'
import './styles/main.css'
import { installContentProtection, isContentProtectionEnabled } from './lib/content-protection'

/* PWA-025: sem copiar conteudo nem atalhos do inspecionar (desligado em dev; ver VITE_SPONSOR_CONTENT_PROTECTION). */
if (isContentProtectionEnabled(import.meta.env)) installContentProtection(document)

const queryClient = new QueryClient({
    queryCache: new QueryCache({
        // Sessao expirada ou revogada em outro aparelho (FR-008): volta para a entrada.
        // Nas telas publicas o 401 e esperado (checagem "ja esta logado?") e nao redireciona.
        onError: (error) => {
            const onPublicPath = PUBLIC_PATHS.some(p => router.state.location.pathname.startsWith(p))
            if (!onPublicPath && error instanceof SponsorApiError && error.code === 'SPONSOR_SESSION_REQUIRED') {
                queryClient.removeQueries({ queryKey: ['sponsor', 'me'] })
                rememberReturnPath(router.state.location.href)
                void router.navigate({ to: '/entrar', replace: true })
            }
        },
    }),
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: true,
        },
    },
})

const router = createAppRouter(queryClient)

/* PWA-033 D-06: historico de atividade (troca de tela e cliques em [data-activity]); fora das telas de entrada. */
const isPublicPath = (pathname: string) => pathname === '/' || PUBLIC_PATHS.some(p => pathname.startsWith(p))
const lastMatch = () => router.state.matches.at(-1)
installActivityTracking(createActivityTracker({ send: postActivity }), {
    subscribe: (fn) => {
        router.subscribe('onResolved', () => {
            const match = lastMatch()
            if (match) fn(match.routeId, match.params as Record<string, unknown>, router.state.location.pathname)
        })
    },
    isPublic: isPublicPath,
    currentRoute: () => {
        const match = lastMatch()
        return match ? routePattern(match.routeId) : undefined
    },
})

/* PWA-014 D-08: service worker so dos avisos. Aviso chegou com o app aberto: conversas, criancas e inicio atualizam na
   hora. Toque no aviso com o app ja aberto: navega aqui dentro (sem recarregar). */
if ('serviceWorker' in navigator) {
    void navigator.serviceWorker.register('/sw.js').catch(() => undefined)
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
        const data = event.data as { type?: unknown; path?: unknown } | null
        if (data?.type === 'sponsor.push') {
            for (const key of [['sponsor', 'threads'], ['sponsor', 'children'], ['sponsor', 'notices']]) void queryClient.invalidateQueries({ queryKey: key })
        }
        if (data?.type === 'sponsor.navigate') {
            const path = safeReturnPath(data.path)
            if (path) router.history.push(path)
        }
    })
}

void i18nReady.then(() => {
    ReactDOM.createRoot(document.getElementById('root')!).render(
        <React.StrictMode>
            <QueryClientProvider client={queryClient}>
                <App router={router} />
            </QueryClientProvider>
        </React.StrictMode>,
    )
})
